import React, { useState, useEffect, useMemo } from "react";
import { Edit, Plus, Trash2, X, Layers, Search } from "lucide-react";
import api from "../api";
import { K } from "../theme";
import { Bg, Bt, Sl, Inp } from "./index";
import { confirmDialog } from "../notify";

// Phase 2 of the Zoho-style salary structure: salary templates.
// A template is a reusable bundle of components with default values
// (e.g. "Engineering L4", "Sales BD"). Phase 2 only ships the
// catalogue + Settings UI; assignment to employees comes in Phase 3.

const CATEGORY_ACCENT = { earning: K.g, deduction: K.r, reimbursement: K.bl, benefit: K.pu };
const CATEGORY_LABEL  = { earning: 'Earning', deduction: 'Deduction', reimbursement: 'Reimbursement', benefit: 'Benefit' };

const fmtCalc = (calc, val) =>
  calc === 'fixed' ? `₹${Number(val || 0).toLocaleString('en-IN')}` :
  calc === 'percent_of_basic' ? `${val}% of Basic` :
  calc === 'percent_of_ctc'   ? `${val}% of CTC` :
  calc === 'percent_of_gross' ? `${val}% of Gross` : `${val}`;

export const SalaryTemplatesManager = () => {
  const [rows, setRows] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [edit, setEdit] = useState(null); // null | 'new' | row
  const [msg, setMsg] = useState("");

  const load = () =>
    Promise.all([
      api.get('/api/salary-templates'),
      api.get('/api/companies?active=true').catch(() => []),
    ]).then(([t, c]) => { setRows(t || []); setCompanies(Array.isArray(c) ? c : (c?.data || [])); })
      .catch(e => setMsg('Error: ' + (e.message || 'load failed')));

  useEffect(() => { load(); }, []);

  const flash = (m, e = false) => { setMsg((e ? 'Error: ' : '') + m); setTimeout(() => setMsg(""), 3000); };

  const del = async (t) => {
    if (!(await confirmDialog(`Delete template "${t.name}"? This cannot be undone.`))) return;
    try { await api.del('/api/salary-templates/' + t.id); flash('Template deleted'); load(); }
    catch (e) { flash(e.message || 'Failed', true); }
  };

  const toggleActive = async (t) => {
    try { await api.put('/api/salary-templates/' + t.id, { active: !t.active }); load(); }
    catch (e) { flash(e.message || 'Failed', true); }
  };

  if (!rows) return <div className="text-t3 text-[14px] p-4">Loading…</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="text-[14px] text-t2 max-w-[640px]">
          Reusable bundles of salary components (e.g. <i>Engineering L4</i>, <i>Sales BD</i>). Use them to apply a consistent salary structure to multiple employees in one click. Templates can be scoped to a company or shared across all companies.
        </div>
        <Bt s="sm" icon={Plus} onClick={() => setEdit('new')}>New Template</Bt>
      </div>

      <div className="rounded-xl border border-b0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[14px]">
            <thead><tr className="bg-bg2">
              <th className="px-2 py-[6px] text-left text-t3 text-[12px] uppercase">Code</th>
              <th className="px-2 py-[6px] text-left text-t3 text-[12px] uppercase">Name</th>
              <th className="px-2 py-[6px] text-left text-t3 text-[12px] uppercase">Company</th>
              <th className="px-2 py-[6px] text-center text-t3 text-[12px] uppercase">Components</th>
              <th className="px-2 py-[6px] text-center text-t3 text-[12px] uppercase">Status</th>
              <th className="px-2 py-[6px] text-right text-t3 text-[12px] uppercase"></th>
            </tr></thead>
            <tbody>
              {rows.map(t => (
                <tr key={t.id} className="border-b border-b0 hover:bg-sh">
                  <td className="px-2 py-[6px] font-mono text-[14px] text-t1">{t.code}</td>
                  <td className="px-2 py-[6px] text-[14px] font-semibold text-t0">
                    {t.name}
                    {t.description && <div className="text-[12px] text-t3 font-normal mt-[1px]">{t.description}</div>}
                  </td>
                  <td className="px-2 py-[6px] text-[14px] text-t2">{t.companyName || <i className="text-t3">All companies</i>}</td>
                  <td className="px-2 py-[6px] text-center text-[14px] text-t1 font-semibold">{t.componentCount ?? 0}</td>
                  <td className="px-2 py-[6px] text-center">
                    {t.active ? <Bg c={K.g} sm dot>Active</Bg> : <Bg c={K.t3} sm>Inactive</Bg>}
                  </td>
                  <td className="px-2 py-[6px] text-right whitespace-nowrap">
                    <Bt v="ghost" s="xs" icon={Edit} onClick={() => setEdit(t)} />
                    <Bt v="ghost" s="xs" onClick={() => toggleActive(t)}>{t.active ? 'Deactivate' : 'Activate'}</Bt>
                    <Bt v="ghost" s="xs" icon={Trash2} onClick={() => del(t)} />
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={6} className="text-center p-6 text-t3 text-[14px]">
                  <Layers size={20} className="inline mb-1 text-t3" /><br/>
                  No templates yet — click <b className="text-t0">New Template</b> to create one.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {msg && <div className={`mt-3 text-[14px] font-semibold ${msg.startsWith('Error') ? 'text-r' : 'text-g'}`}>{msg}</div>}

      {edit && <TemplateModal cur={edit === 'new' ? null : edit} companies={companies}
                              onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
};

// ── Add / Edit template modal ──
// Loads the full template (with components) on open when editing; lets you
// pick from the component library and set per-template values.
const TemplateModal = ({ cur, companies, onClose, onSaved }) => {
  const isEdit = !!cur?.id;
  const [meta, setMeta] = useState({
    code: cur?.code || '', name: cur?.name || '',
    description: cur?.description || '',
    companyId: cur?.companyId ? String(cur.companyId) : '',
    active: cur ? !!cur.active : true,
  });
  const [lines, setLines] = useState([]);         // selected components for this template
  const [library, setLibrary] = useState([]);     // master salary_components
  const [loaded, setLoaded] = useState(false);
  const [picker, setPicker] = useState(false);
  const [saving, setSaving] = useState(false); const [err, setErr] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const lib = await api.get('/api/salary-components?active_only=1');
        const det = isEdit ? await api.get('/api/salary-templates/' + cur.id) : null;
        if (cancelled) return;
        setLibrary(lib || []);
        setLines((det?.components || []).map(c => ({
          componentCode: c.componentCode,
          name: c.componentName,
          category: c.componentCategory,
          calcType: c.componentCalcType,
          value: String(c.value ?? 0),
          active: c.active !== false,
          sortOrder: c.sortOrder ?? 100,
        })));
        setLoaded(true);
      } catch (e) { setErr(e.message || 'Load failed'); setLoaded(true); }
    })();
    return () => { cancelled = true; };
  }, [isEdit, cur?.id]);

  const addComponent = (libRow) => {
    if (lines.find(l => l.componentCode === libRow.code)) return;
    setLines(p => [...p, {
      componentCode: libRow.code, name: libRow.name, category: libRow.category,
      calcType: libRow.calcType, value: String(libRow.defaultValue ?? 0),
      active: true, sortOrder: 100 + p.length,
    }]);
  };
  const removeLine = (code) => setLines(p => p.filter(l => l.componentCode !== code));
  const setLineValue = (code, v) => setLines(p => p.map(l => l.componentCode === code ? { ...l, value: v } : l));

  const grouped = useMemo(() => {
    const out = { earning: [], deduction: [], reimbursement: [], benefit: [] };
    lines.forEach(l => { (out[l.category] || (out[l.category] = [])).push(l); });
    return out;
  }, [lines]);

  const save = async () => {
    if (!meta.name.trim()) return setErr('Name is required');
    if (!isEdit && !meta.code.trim()) return setErr('Code is required');
    setSaving(true); setErr('');
    const body = {
      name: meta.name.trim(),
      description: meta.description || null,
      companyId: meta.companyId ? parseInt(meta.companyId) : null,
      active: !!meta.active,
      components: lines.map((l, i) => ({
        componentCode: l.componentCode,
        value: parseFloat(l.value) || 0,
        active: l.active !== false,
        sortOrder: l.sortOrder || (100 + i),
      })),
    };
    try {
      if (isEdit) await api.put('/api/salary-templates/' + cur.id, body);
      else await api.post('/api/salary-templates', { code: meta.code.trim(), ...body });
      onSaved();
    } catch (e) { setErr(e.message || 'Save failed'); } finally { setSaving(false); }
  };

  const lbl = "text-[14px] text-t2 mb-[3px]";
  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3" onMouseDown={onClose}>
      <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-4 sm:p-6 w-full max-w-[1244px] max-h-[92vh] overflow-auto" onMouseDown={e => e.stopPropagation()}>
        <div className="flex justify-between mb-3">
          <span className="text-[18px] font-extrabold text-t0">{isEdit ? 'Edit Salary Template' : 'New Salary Template'}</span>
          <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer"><X size={18} /></button>
        </div>

        <div className="text-[14px] font-bold text-t3 uppercase tracking-wide mb-2">Template Details</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-[10px] mb-4">
          <div><div className={lbl}>Code <span className="text-r">*</span></div>
            <Inp value={meta.code} disabled={isEdit}
                 onChange={v => setMeta(p => ({ ...p, code: (v || '').toUpperCase().replace(/[^A-Z0-9_]/g, '_').slice(0, 24) }))}
                 placeholder="e.g. ENG_L4" />
            <div className="text-[12px] text-t3 mt-[2px]">{isEdit ? 'Code cannot be changed.' : 'A short identifier — letters, digits, underscore.'}</div>
          </div>
          <div><div className={lbl}>Name <span className="text-r">*</span></div>
            <Inp value={meta.name} onChange={v => setMeta(p => ({ ...p, name: v }))} placeholder="e.g. Engineering L4" />
          </div>
          <div className="sm:col-span-2"><div className={lbl}>Description</div>
            <Inp value={meta.description} onChange={v => setMeta(p => ({ ...p, description: v }))} placeholder="What this template is for…" />
          </div>
          <div><div className={lbl}>Company scope</div>
            <Sl options={[{ v: '', l: 'All companies' }, ...companies.map(c => ({ v: String(c.id), l: c.name }))]}
                value={meta.companyId} onChange={v => setMeta(p => ({ ...p, companyId: v }))} />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!meta.active} onChange={e => setMeta(p => ({ ...p, active: e.target.checked }))} />
              <span className="text-[14px] text-t1">Active</span>
            </label>
          </div>
        </div>

        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="text-[14px] font-bold text-t3 uppercase tracking-wide">Components in this template</div>
          <Bt s="xs" icon={Plus} onClick={() => setPicker(true)}>Add component</Bt>
        </div>

        {!loaded ? (
          <div className="text-t3 text-[14px] p-4">Loading library…</div>
        ) : lines.length === 0 ? (
          <div className="rounded-xl border border-dashed border-b0 p-4 text-center text-t3 text-[14px]">
            No components yet — click <b className="text-t0">Add component</b> to bundle earnings, deductions, reimbursements, or benefits into this template.
          </div>
        ) : (
          <div className="grid gap-3 mb-4">
            {['earning', 'deduction', 'reimbursement', 'benefit'].map(cat => {
              const list = grouped[cat] || [];
              if (list.length === 0) return null;
              return (
                <div key={cat}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-[5px] h-[14px] rounded-full" style={{ background: CATEGORY_ACCENT[cat] }} />
                    <span className="text-[14px] font-bold text-t1">{CATEGORY_LABEL[cat]}s</span>
                  </div>
                  <div className="rounded-lg border border-b0 overflow-hidden">
                    <table className="w-full border-collapse text-[14px]">
                      <thead><tr className="bg-bg2">
                        <th className="px-2 py-1 text-left text-t3 text-[12px] uppercase">Code</th>
                        <th className="px-2 py-1 text-left text-t3 text-[12px] uppercase">Name</th>
                        <th className="px-2 py-1 text-left text-t3 text-[12px] uppercase">Type</th>
                        <th className="px-2 py-1 text-right text-t3 text-[12px] uppercase">Value</th>
                        <th className="px-2 py-1 text-right text-t3 text-[12px] uppercase"></th>
                      </tr></thead>
                      <tbody>
                        {list.map(l => (
                          <tr key={l.componentCode} className="border-b border-b0">
                            <td className="px-2 py-1 font-mono text-[14px] text-t1">{l.componentCode}</td>
                            <td className="px-2 py-1 text-[14px] text-t0">{l.name}</td>
                            <td className="px-2 py-1 text-[14px] text-t2">
                              {l.calcType === 'fixed' ? 'Fixed (₹)' :
                               l.calcType === 'percent_of_basic' ? '% of Basic' :
                               l.calcType === 'percent_of_ctc'   ? '% of CTC' :
                               l.calcType === 'percent_of_gross' ? '% of Gross' : l.calcType}
                            </td>
                            <td className="px-2 py-1 text-right">
                              <input
                                type="number"
                                value={l.value}
                                onChange={e => setLineValue(l.componentCode, e.target.value)}
                                className="bg-bg2 border border-b1 rounded px-2 py-[3px] text-right text-[14px] text-t0 w-[110px] focus:outline-none focus:border-bl"
                              />
                            </td>
                            <td className="px-2 py-1 text-right">
                              <Bt v="ghost" s="xs" icon={Trash2} onClick={() => removeLine(l.componentCode)} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {err && <div className="text-[14px] text-r mb-2">{err}</div>}
        <div className="flex gap-[6px]">
          <Bt full onClick={save} disabled={saving}>{saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Create template')}</Bt>
          <Bt v="secondary" full onClick={onClose}>Cancel</Bt>
        </div>
      </div>

      {picker && (
        <ComponentPicker
          library={library}
          alreadyAdded={new Set(lines.map(l => l.componentCode))}
          onClose={() => setPicker(false)}
          onPick={(row) => { addComponent(row); setPicker(false); }}
        />
      )}
    </div>
  );
};

// ── Library picker ──
const ComponentPicker = ({ library, alreadyAdded, onClose, onPick }) => {
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('all');
  const filtered = library
    .filter(c => cat === 'all' || c.category === cat)
    .filter(c => !alreadyAdded.has(c.code))
    .filter(c => {
      if (!q.trim()) return true;
      const k = q.trim().toLowerCase();
      return c.name.toLowerCase().includes(k) || c.code.toLowerCase().includes(k);
    });

  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[210] flex items-center justify-center p-3" onMouseDown={onClose}>
      <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-4 w-full max-w-[936px] max-h-[88vh] overflow-auto" onMouseDown={e => e.stopPropagation()}>
        <div className="flex justify-between mb-2">
          <span className="text-[17px] font-extrabold text-t0">Pick a component</span>
          <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer"><X size={18} /></button>
        </div>
        <div className="flex gap-2 mb-3">
          <div className="relative flex-1">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-t3" />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by name or code…"
                   className="w-full bg-bg2 border border-b1 rounded pl-7 pr-2 py-[6px] text-[14px] text-t0 focus:outline-none focus:border-bl" />
          </div>
          <Sl
            options={[
              { v: 'all', l: 'All' },
              { v: 'earning', l: 'Earnings' },
              { v: 'deduction', l: 'Deductions' },
              { v: 'reimbursement', l: 'Reimbursements' },
              { v: 'benefit', l: 'Benefits' },
            ]}
            value={cat}
            onChange={setCat}
          />
        </div>
        <div className="rounded-lg border border-b0 overflow-hidden">
          <table className="w-full border-collapse text-[14px]">
            <thead><tr className="bg-bg2">
              <th className="px-2 py-1 text-left text-t3 text-[12px] uppercase">Code</th>
              <th className="px-2 py-1 text-left text-t3 text-[12px] uppercase">Name</th>
              <th className="px-2 py-1 text-left text-t3 text-[12px] uppercase">Category</th>
              <th className="px-2 py-1 text-left text-t3 text-[12px] uppercase">Default</th>
              <th className="px-2 py-1 text-right text-t3 text-[12px] uppercase"></th>
            </tr></thead>
            <tbody>
              {filtered.map(c => (
                <tr key={c.code} className="border-b border-b0 hover:bg-sh">
                  <td className="px-2 py-1 font-mono text-[14px] text-t1">{c.code}</td>
                  <td className="px-2 py-1 text-[14px] text-t0">{c.name}</td>
                  <td className="px-2 py-1 text-[14px]">
                    <Bg c={CATEGORY_ACCENT[c.category] || K.t3} sm>{CATEGORY_LABEL[c.category] || c.category}</Bg>
                  </td>
                  <td className="px-2 py-1 text-[14px] text-t2">{fmtCalc(c.calcType, c.defaultValue)}</td>
                  <td className="px-2 py-1 text-right"><Bt s="xs" onClick={() => onPick(c)}>Add</Bt></td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={5} className="text-center p-3 text-t3 text-[14px]">
                  No matches{alreadyAdded.size > 0 ? ' — already-added components are hidden.' : '.'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
